import React, { SyntheticEvent, useEffect, useState } from 'react';
import RecallTable from './RecallTable/RecallTable';
import { Autocomplete } from '@mui/material';
import AddRecallModal from './AddRecallModal/AddRecallModal';
import RecallGroupingModal from './RecallGroupingModal/RecallGroupingModal';
import { IRecall } from '../../../store/reducers/recall/types';
import { autocompleteRender } from '../../../utils/autocompleteRenders';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/rootReducer';
import { loadAllAssignedServiceRequests } from '../../../store/reducers/serviceRequests/actions';
import { IAssignedServiceRequest } from '../../../store/reducers/serviceRequests/types';
import { updateDefaultRecallOpsCode } from '../../../store/reducers/serviceCenters/actions';
import { useModal } from '../../../hooks/useModal/useModal';
import { useException } from '../../../hooks/useException/useException';
import { useSCs } from '../../../hooks/useSCs/useSCs';
import { useInputStyles, useStyles } from './styles';
import { SearchDebounced } from '../../../components/formControls/SearchDebounced/SearchDebounced';
import { setRecallPageData, setRecallSearch } from '../../../store/reducers/recall/actions';
import MakesForm from './MakesForm/MakesForm';
import { loadMakesAll } from '../../../store/reducers/vehicleDetails/actions';
import RecallSyncProgress from './RecallSyncProgress/RecallSyncProgress';
import { useRecallOperations } from './useRecallOperations';

const RecallParts = () => {
  const [currentItem, setCurrentItem] = useState<IRecall | null>(null);
  const [selectedOpsCode, setSelectedOpsCode] = useState<IAssignedServiceRequest | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [defaultOpsDropdownOpenCount, setDefaultOpsDropdownOpenCount] = useState(0);
  const { selectedSC } = useSCs();
  const { allAssignedList } = useSelector((state: RootState) => state.serviceRequests);
  const { isOpen, onOpen, onClose } = useModal();
  const { isOpen: isGroupingOpen, onOpen: onOpenGrouping, onClose: closeGrouping } = useModal();
  const { classes } = useStyles();
  const { classes: inputClasses } = useInputStyles();
  const dispatch = useDispatch();
  const showError = useException();
  const { target, finishedOperation, dismissFinished, isOperationRunning } = useRecallOperations(
    selectedSC?.id
  );

  useEffect(() => {
    return () => {
      dispatch(setRecallSearch(''));
      dispatch(setRecallPageData({ pageIndex: 0, pageSize: 10 }));
    };
  }, []);

  useEffect(() => {
    if (selectedSC && allAssignedList) {
      const opsCode = allAssignedList.find(item => item.id === selectedSC.recallServiceRequestId);
      setSelectedOpsCode(opsCode ?? null);
    }
  }, [allAssignedList, selectedSC]);

  useEffect(() => {
    if (selectedSC) {
      dispatch(loadAllAssignedServiceRequests(selectedSC.id));
      dispatch(loadMakesAll(selectedSC.id));
    }
  }, [selectedSC]);

  // clear the search term when navigating to another page
  useEffect(() => {
    return () => {
      dispatch(setRecallSearch(''));
    };
  }, []);

  const handleAddRecall = () => {
    onOpen();
  };

  const onCloseGrouping = () => {
    closeGrouping();
    setCurrentItem(null);
  };

  const onSRChange = (e: SyntheticEvent, value: IAssignedServiceRequest | null) => {
    setLoading(true);
    if (selectedSC && value) {
      setSelectedOpsCode(value);
      try {
        dispatch(updateDefaultRecallOpsCode(selectedSC.id, value?.id));
      } catch (err) {
        showError(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const onSearch = () => {
    dispatch(setRecallSearch(search.trim()));
    dispatch(setRecallPageData({ pageIndex: 0, pageSize: 10 }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  return (
    <>
      <div className={classes.wrapper}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <Autocomplete
            classes={inputClasses}
            style={{ width: 210 }}
            loading={loading}
            value={selectedOpsCode}
            options={allAssignedList}
            onOpen={() => setDefaultOpsDropdownOpenCount(prev => prev + 1)}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            getOptionLabel={o => o.serviceRequest.code}
            onChange={onSRChange}
            renderInput={autocompleteRender({
              label: 'default recall op code:',
              placeholder: 'Select Op Code',
            })}
          />
          <MakesForm
            hasDefaultRecallOpsCode={Boolean(selectedOpsCode)}
            clearSelectionErrorTrigger={defaultOpsDropdownOpenCount}
            isSyncBlocked={isOperationRunning}
          />
        </div>
        <SearchDebounced
          onSearch={onSearch}
          onChange={handleSearchChange}
          style={{ height: 40, width: 334 }}
          value={search}
          placeholder="Search ..."
        />
        {/*<Button*/}
        {/*  className={classes.button}*/}
        {/*  color="primary"*/}
        {/*  variant="contained"*/}
        {/*  onClick={handleAddRecall}*/}
        {/*>*/}
        {/*  Add Recall*/}
        {/*</Button>*/}
      </div>
      <RecallSyncProgress
        target={target}
        finishedOperation={finishedOperation}
        onDismiss={dismissFinished}
      />
      <RecallTable
        onOpenModal={handleAddRecall}
        onOpenGrouping={onOpenGrouping}
        currentItem={currentItem}
        setCurrentItem={setCurrentItem}
      />
      <AddRecallModal
        open={isOpen}
        editingItem={currentItem}
        onClose={onClose}
        setEditingItem={setCurrentItem}
      />
      <RecallGroupingModal open={isGroupingOpen} recall={currentItem} onClose={onCloseGrouping} />
    </>
  );
};

export default RecallParts;
